import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLang } from "@/i18n";
import { useSwipe } from "@/hooks/useSwipe";

const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const swipe = useSwipe(
    () => onNavigate((index + 1) % images.length),
    () => onNavigate((index - 1 + images.length) % images.length)
  );
  const { t } = useLang();
  const open = index !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate((index + 1) % images.length);
      if (e.key === "ArrowLeft") onNavigate((index - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, images.length, onClose, onNavigate]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[95] bg-anthracite/95 flex items-center justify-center p-4 md:p-12"
          data-testid="gallery-lightbox"
          onClick={onClose}
          {...swipe}
        >
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label={t.gallery.close}
            className="absolute top-5 right-5 p-2 text-ivory/80 hover:text-ivory transition-colors duration-200"
          >
            <X className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((index - 1 + images.length) % images.length);
            }}
            data-testid="lightbox-prev"
            aria-label={t.gallery.prev}
            className="absolute left-3 md:left-8 p-2 text-ivory/80 hover:text-ivory transition-colors duration-200"
          >
            <ChevronLeft className="w-9 h-9" />
          </button>
          <motion.img
            key={images[index].src}
            src={images[index].src}
            alt={images[index].alt}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[82vh] max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((index + 1) % images.length);
            }}
            data-testid="lightbox-next"
            aria-label={t.gallery.next}
            className="absolute right-3 md:right-8 p-2 text-ivory/80 hover:text-ivory transition-colors duration-200"
          >
            <ChevronRight className="w-9 h-9" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
